/**
 * Entity / snapshot data shapes — the slice of Unity's `EntityData` +
 * `EntitySnapshotData` (`Models/Data/EntityData.cs`,
 * `Models/Data/EntitySnapshotData.cs`) that the M4 UI shell + inspector
 * actually read. Snapshots are how Unity addresses parts vs roots; we keep
 * the same vocabulary so server contracts line up later.
 *
 * Anything missing here (materials, animation settings, authoring poses,
 * collision/gravity, …) lands in M7 with the per-context inspector views.
 */

import type { PartAlias } from "./aliasData";

/** Coarse-grained kind used for icons + filters in the hierarchy. */
export type EntityKind = "mesh" | "light" | "camera" | "annotation" | "group";

/** 3-component vector used for position / euler rotation / scale. */
export type Vec3 = readonly [number, number, number];

/** Per-snapshot transform + visibility — mutable pieces of `EntitySnapshotData`. */
export interface EntityTransform {
  readonly position: Vec3;
  /**
   * Euler degrees in Unity's **ZXY** application order — `Quaternion.Euler`
   * composes `Ry(y) · Rx(x) · Rz(z)`. The component ORDER of the tuple is
   * (x, y, z); the letters name axes, not the order they are applied in.
   *
   * Do not build an XYZ euler from this (the comment here used to say "XYZ",
   * which is the single most misleading way to describe it). Convert through
   * `@models/utils/unityBasis`.
   */
  readonly rotation: Vec3;
  readonly scale: Vec3;
}

/**
 * `EntitySnapshotData` — addressable node within an entity (root or part).
 * Unity calls `IsRoot === true` snapshots "the entity itself" and the
 * remaining ones "parts". We keep both so the hierarchy can render the same
 * tree the desktop app does.
 */
export interface EntitySnapshot {
  readonly id: string;
  readonly name: string;
  readonly displayName: string;
  readonly kind: EntityKind;
  /** Unity `EntitySnapshotData.PartID` — the part-row join key (library
   *  `PartData.MaterialIDs` restore, hierarchy addressing). `null` when
   *  the wire row carried no part id. */
  readonly partId: string | null;
  readonly isRoot: boolean;
  /**
   * Unity `EntitySnapshotData.HasMergedChildren` — true for a merged node
   * whose child renderers were combined into it. Such a node owns no
   * renderer of its own and carries no `MaterialIDs`; the material-apply
   * path must skip it rather than blank the merged children (AI-1173).
   */
  readonly hasMergedChildren: boolean;
  readonly visibility: boolean;
  readonly forcedVisibility: boolean;
  readonly isLocked: boolean;
  /**
   * Unity `EntitySnapshotData.IsCollisionEnabled` (kinematics). The web port
   * has no physics — this is carried purely so
   * `IProjectModelSlim.isSmoothTransitionSupported` can reproduce Unity's
   * veto (`ProjectModelSlim.cs:672-679`): a collision-enabled entity in the
   * outgoing scene forces the full loading screen.
   */
  readonly isCollisionEnabled: boolean;
  /** Unity snapshot explosion factor. Root snapshots own the persisted value. */
  readonly explosionFactor: number;
  readonly transform: EntityTransform;
  /** Original import pose used by Campfire placement reset actions. */
  readonly resetTransform: EntityTransform;
  /** Unity `EntitySnapshotData.MaterialIDs`; first slice uses the first material slot. */
  readonly materialIds: ReadonlyArray<string>;
  /**
   * Unity `EntitySnapshotData.persistentSpinJson` — the constant-spin payload
   * stored VERBATIM (see `models/data/persistentSpinData.ts`). `null` = no
   * spin (Unity omits the Dynamo attribute). The persisted `rotation` above
   * is always the spin-AUTHORED rotation (spin offset excluded).
   */
  readonly persistentSpinJson: string | null;
  /**
   * Unity `EntitySnapshotData.persistentHighlightJson` — the persistent-highlight
   * payload stored VERBATIM (see `models/data/persistentHighlightData.ts`).
   * `null` = not highlighted (Unity omits the Dynamo attribute).
   */
  readonly persistentHighlightJson: string | null;
  /**
   * Part name aliases for this project's copy of the part (`aliasData.ts`).
   * Always normalized and never null — an empty array is "no aliases", which
   * keeps every consumer (button label, sync compare, bake) branch-free.
   */
  readonly aliases: ReadonlyArray<PartAlias>;
  /**
   * Root snapshots only: the library `AssetVersion` this placement is synced
   * to. `undefined` = unknown (placed before the watermark existed), which
   * `ProjectModelLibrarySync` back-fills rather than treating as behind.
   */
  readonly librarySyncedVersion?: number;
  readonly children: ReadonlyArray<EntitySnapshot>;
  /**
   * Raw `PartData.HierachyID` (with leading marker + trailing comma) used by
   * the renderer to walk the cloned GLB hierarchy and overwrite each part's
   * local transform from the snapshot. Mirrors Unity's
   * `ProjectModelSlim.GetPartByHierarchyID` traversal in
   * `ApplyEntitySnapshots` (`ProjectModelSlim.cs:1200-1226`,
   * `ProjectModelSlim.cs:1249-1273`).
   *
   * Format example: `"1,0,2,3,"`. The leading segment is a root marker, the
   * trailing empty segment is part of Unity's split convention, and the
   * inner segments are 0-based child indices into the cleaned asset root.
   */
  readonly hierachyID: string | null;
}

/** Editable subset of `EntitySnapshotData` currently backed by the web port. */
export interface EntitySnapshotPatch {
  readonly name?: string;
  readonly displayName?: string;
  readonly visibility?: boolean;
  readonly forcedVisibility?: boolean;
  readonly isLocked?: boolean;
  /**
   * Merge state for this node. Maps to `UpdateEntityFlags.Merge` (1 << 13) and
   * the Dynamo `Merged` attribute. Write it through `IMergeModel`, not
   * directly: un-merging also has to clear the flag on every descendant
   * (`ProjectModel_Entities.cs:322-334`), and that cascade is the model's job.
   */
  readonly hasMergedChildren?: boolean;
  readonly explosionFactor?: number;
  readonly materialIds?: ReadonlyArray<string>;
  readonly transform?: Partial<EntityTransform>;
  /**
   * Constant-spin payload: a JSON string sets `PersistentSpinJson`, `null`
   * REMOVEs the attribute (Unity's full-row put simply omits it), `undefined`
   * leaves it untouched. Maps to `UpdateEntityFlags.Spin` (1 << 16).
   */
  readonly persistentSpinJson?: string | null;
  /**
   * Runtime persistent-highlight payload: a JSON string applies it, `null`
   * clears it, and `undefined` leaves it untouched. Web reads legacy stored
   * values but deliberately does not persist this field to DynamoDB.
   */
  readonly persistentHighlightJson?: string | null;
  /**
   * Replaces the whole alias list (there is no per-entry patch — the editor
   * always submits the full list, and a list-valued Dynamo attribute is
   * written wholesale anyway). An EMPTY array clears them; `undefined` leaves
   * them untouched. Maps to `UpdateEntityFlags.Aliases` (1 << 17).
   */
  readonly aliases?: ReadonlyArray<PartAlias>;
  /**
   * Library sync watermark (root snapshots). Maps to the web-only patch flag
   * `1 << 18` — 17 is Aliases and Unity's `UpdateEntityFlags` reserves 29-31,
   * so 18 is the next free bit. Unity receivers test named members with `&`,
   * so a bit they do not know is ignored rather than misread.
   */
  readonly librarySyncedVersion?: number;
}

/** Editable entity-level settings stored separately from snapshot rows. */
export interface EntitySettingsPatch {
  readonly name?: string;
  readonly displayName?: string;
  readonly description?: string;
  readonly units?: string;
  readonly flipNormals?: boolean;
  readonly visualizePoints?: boolean;
  readonly animation?: {
    readonly take?: string;
    readonly playback?: string;
    readonly direction?: string;
    readonly speedPercent?: number;
    readonly autoplay?: boolean;
  };
  readonly kinematics?: {
    readonly collisions?: boolean;
    readonly haptics?: boolean;
    readonly grabIndicators?: boolean;
    readonly collideVisualization?: boolean;
    readonly forceVisualization?: boolean;
    readonly visualizationType?: string;
    readonly visualizePoints?: boolean;
  };
  readonly compatibilityVariants?: Readonly<Record<string, boolean>>;
}

/** Unity `AnimationPlaybackType` serialized values. */
export const ENTITY_ANIMATION_PLAYBACK_TYPE = Object.freeze({
  Loop: 0,
  Once: 1,
  PingPong: 2,
} as const);

export type EntityAnimationPlaybackType =
  (typeof ENTITY_ANIMATION_PLAYBACK_TYPE)[keyof typeof ENTITY_ANIMATION_PLAYBACK_TYPE];

/** Runtime subset of Unity `AnimationSettings`. */
export interface EntityAnimationSettings {
  readonly autoplay: boolean;
  readonly playbackType: EntityAnimationPlaybackType;
  /** Signed playback multiplier; negative values play backward. */
  readonly speed: number;
  /** Zero-based clip index; `-1` means the imported default/first clip. */
  readonly clipIndex: number;
}

export const DEFAULT_ENTITY_ANIMATION_SETTINGS: EntityAnimationSettings = Object.freeze({
  autoplay: true,
  playbackType: ENTITY_ANIMATION_PLAYBACK_TYPE.Loop,
  speed: 1,
  clipIndex: -1,
});

/**
 * `EntityData` — the per-scene record. Owns the snapshot tree (roots +
 * parts). Asset/material ids will join later (M7+).
 */
export interface EntityData {
  readonly id: string;
  readonly sceneId: string;
  readonly assetId: string | null;
  /**
   * Unity `EntitySettingsData.DisplayOrder` — the persisted scene ordering key
   * (`ProjectDataModel.GetEntitiesBySceneID` is `OrderBy(DisplayOrder)`).
   * Missing settings rows read as 0, matching Unity's
   * `new EntitySettingsData(entityID)` fallback.
   */
  readonly displayOrder: number;
  readonly animationSettings: EntityAnimationSettings;
  readonly snapshots: ReadonlyArray<EntitySnapshot>;
}

/**
 * `DisplayOrder` for an entity about to be added to `entities`. Port of Unity's
 * `ProjectModel.AssignDisplayOrder` (`ProjectModel_UploadAsset.cs:314`):
 * `max(DisplayOrder) + inFlightUploads + 1`, seeded from `-1` so the first
 * model in an empty scene gets 0. Unity's in-flight term covers concurrent
 * uploads that aren't in the data model yet; web imports are awaited one at a
 * time, so it is always 0 here.
 *
 * Counting the entities instead collides with an existing order as soon as a
 * model has been deleted: orders 0,1,2 minus the middle leaves 0,2, and the
 * count is 2. The tie then resolves by load order, which seats the model
 * differently than Unity does and shifts every part path after it.
 */
export function nextEntityDisplayOrder(
  entities: ReadonlyArray<EntityData>,
): number {
  let highest = -1;
  for (const entity of entities) {
    if (entity.displayOrder > highest) highest = entity.displayOrder;
  }
  return highest + 1;
}

/**
 * Maps every snapshot INSIDE a merged subtree to the merged node that stands
 * in for it. A snapshot absent from the map is one the user can still address
 * directly (it is either outside any merged subtree, or it is the merged node
 * itself).
 *
 * Unity has no such map because it deletes the evidence: merging destroys the
 * `CampfireExposeToEditor` / `RuntimeObjectProperties` components on every
 * descendant (`MergeModel.DestroyMergedRuntimeObjects`), and the merged node
 * absorbs their renderers (`RuntimeObjectProperties.MergeChildren`). From then
 * on the subtree simply does not exist for anything that walks editor objects
 * — the hierarchy tree (`HierarchyViewModel.LoadHierarchyToItem` skips
 * children with no `CampfireExposeToEditor`), every raycast
 * (`MergeModel.GetRuntimeObjectInParent` returns the nearest surviving one,
 * i.e. the merged node), and the AI scene listing
 * (`AIContextEngineModel.BuildNodeFlat`).
 *
 * The web keeps the full snapshot tree in memory — the renderer needs each
 * descendant to place its glTF node — so the pruning that Unity gets for free
 * has to be expressed. This index is that expression, and the value follows
 * Unity's `GetHierarchyVisibleObject`: with nested merges the OUTERMOST merged
 * ancestor wins.
 */
export function buildMergedAncestorIndex(
  snapshots: ReadonlyArray<EntitySnapshot>,
): ReadonlyMap<string, string> {
  const out = new Map<string, string>();
  const walk = (
    nodes: ReadonlyArray<EntitySnapshot>,
    mergedAncestorId: string | null,
  ): void => {
    for (const node of nodes) {
      if (mergedAncestorId) out.set(node.id, mergedAncestorId);
      if (node.children.length === 0) continue;
      walk(node.children, mergedAncestorId ?? (node.hasMergedChildren ? node.id : null));
    }
  };
  walk(snapshots, null);
  return out;
}

/**
 * Name the importer gives the CAD-origin marker, and the hierarchy id it always
 * lands on (first child of the entity root).
 */
export const ORIGIN_SNAPSHOT_NAME = "Origin";
const K_ORIGIN_HIERARCHY_ID = "0,0,";

/**
 * Web stand-in for Unity's `go.CompareTag(CampfireTags.Origin)`. The marker
 * carries no geometry, so it is excluded from focus framing, cannot join a
 * selection set, and cannot be isolated
 * (`EntityContextView.CanAddToSelectionSet`, `:764`).
 */
export function isOriginSnapshot(snapshot: EntitySnapshot): boolean {
  return snapshot.name === ORIGIN_SNAPSHOT_NAME
    && snapshot.hierachyID === K_ORIGIN_HIERARCHY_ID;
}

/** Same index across every entity in a scene, keyed by snapshot id. */
export function buildSceneMergedAncestorIndex(
  entities: ReadonlyArray<EntityData>,
): ReadonlyMap<string, string> {
  const out = new Map<string, string>();
  for (const entity of entities) {
    for (const [id, ancestorId] of buildMergedAncestorIndex(entity.snapshots)) {
      out.set(id, ancestorId);
    }
  }
  return out;
}
